// src/utils/formGridEditor.js
//
// The arithmetic of the form layout editor's grid (KPE-023, KPR-585): how a
// group is drawn on twelve columns, which column a pointer is over, where a
// dropped element would land, and what a resize stops at. Pure — no React, no
// DOM (the component measures rectangles and passes numbers in) — so what a
// drag does is testable without a browser, which jsdom cannot drive.
//
// The layout is FLOW WITH WIDTHS: elements fill a row left to right, wrap when
// the next one does not fit, and an element may start a fresh row (`newRow`).
// There is no free 2D placement, so a drop is only ever "this slot in the
// order", plus whether that slot starts a row. Everything here reads and writes
// entries through the vendored limits module (`entryOf`, `makeEntry`), and the
// widths through `utils/formWidth`, which is what FormWidthControl reads: the
// handle, the keys and the control cannot disagree about a limit.

import { COLUMNS, entryOf } from './formGrid/limits';
import { cellSpans } from './formGridCells';
import { aboveMaximumReason, belowMinimumReason } from './formWidth';
import { moveFormElement, setFormElementPlacement } from './formLayout';

const entriesOf = group => (Array.isArray(group?.elements) ? group.elements : []);
const groupsOf = body => (Array.isArray(body?.groups) ? body.groups : []);
const namesOf = group => entriesOf(group).map(raw => entryOf(raw).element).filter(Boolean);

/**
 * What the editor needs to know about the card to draw a group:
 *
 *   { defs: Map(name -> element) }
 *
 * The two halves of a date range are two cells like any others: the form
 * draws them as two fields (KPR-626).
 */

/**
 * The cells a group draws, in order: `[{ name, span, newRow, kind }]` — `span`
 * the columns the FORM draws it at (the renderer's own clamp, `cellSpans`), so
 * the canvas cannot disagree with the form. `kind` is
 *   'field'   an element of the card, drawn at its width;
 *   'unknown' a name this page's card cache cannot describe: a full-width stub
 *             it keeps its place as (it has no limits to hold it to).
 */
export function groupCells(group, ctx) {
	const cells = [];
	for (const raw of entriesOf(group)) {
		const { element: name, span, newRow } = entryOf(raw);
		if (!name) continue;
		const element = ctx.defs.get(name);
		if (!element) {
			cells.push({ name, span: COLUMNS, newRow: true, kind: 'unknown' });
			continue;
		}
		const drawn = cellSpans(element, span).wide;
		cells.push({ name, span: drawn, newRow, kind: 'field' });
	}
	return cells;
}

/**
 * Where the browser's grid would put each cell: rows of
 * `[{ index, name, start, span }]` (`start` the zero-based column). A cell
 * starts a row when it asks to (`newRow`) or when it does not fit in what is
 * left of the current one, the same rule the CSS grid and the form's own
 * `.form-grid` follow.
 */
export function flowRows(cells, columns = COLUMNS) {
	const rows = [];
	let row = [];
	let used = 0;
	cells.forEach((cell, index) => {
		const span = Math.max(1, Math.min(columns, Number.isInteger(cell.span) ? cell.span : columns));
		if (row.length && (cell.newRow || used + span > columns)) {
			rows.push(row);
			row = [];
			used = 0;
		}
		row.push({ index, name: cell.name, start: used, span });
		used += span;
	});
	if (row.length) rows.push(row);
	return rows;
}

// ---- resizing -------------------------------------------------------------

/**
 * The whole number of columns a pointer at `x` asks of a cell whose left edge
 * is `left`, from the cell's own measurements: its width now (`width`) at
 * `span` columns, and the gap between columns. A cell of `s` columns is
 * `s * step - gap` wide, so the pitch of one column (`step`) is read off the
 * cell itself and no knowledge of the surrounding list is needed. Returns null
 * when the cell has no measurable width (a jsdom node, a hidden one).
 */
export function spanFromPointer({ x, left, width, span, gap = 0 }) {
	if (!(width > 0) || !(span >= 1)) return null;
	const step = (width + gap) / span;
	const columns = Math.round((x - left + gap) / step);
	return Math.max(1, Math.min(COLUMNS, columns));
}

/**
 * `raw` columns held to an element's limits (`widthLimits`):
 * `{ span, limited, reason }` — `limited` is 'min' or 'max' when the request
 * was outside them (the span is then the limit), and `reason` the sentence
 * that says why, or null.
 */
export function limitSpan(limits, raw) {
	if (raw < limits.min) return { span: limits.min, limited: 'min', reason: belowMinimumReason(limits) };
	if (raw > limits.max) return { span: limits.max, limited: 'max', reason: aboveMaximumReason(limits) };
	return { span: raw, limited: null, reason: null };
}

/**
 * One column wider (+1) or narrower (-1) from `current` — the keys' resize
 * step — held to the same limits: `{ span, changed, limited, reason }`. A
 * `current` outside the limits (a stored width the element's type can no
 * longer hold) is first taken to the nearest width they allow, and `changed`
 * is measured from there: a widen key on an element
 * already at its widest says why instead of narrowing it.
 */
export function stepSpan(limits, current, delta) {
	const from = Math.min(limits.max, Math.max(limits.min, current));
	const result = limitSpan(limits, from + delta);
	return { ...result, changed: result.span !== from };
}

// ---- dropping -------------------------------------------------------------

/** Which side of the target cell a drop lands on: 'before' or 'after'. */
export function dropSide({ rect, x, y, axis }) {
	if (axis === 'y') return y < rect.top + rect.height / 2 ? 'before' : 'after';
	return x < rect.left + rect.width / 2 ? 'before' : 'after';
}

/**
 * Which way the halves of a cell run: a cell that takes (nearly) the whole
 * width of its list is one in a column, so its halves are top and bottom ('y');
 * a narrower one has neighbours beside it, so they are left and right ('x').
 */
export function dropAxis({ cell, list, slack = 24 }) {
	return cell.width >= list.width - slack ? 'y' : 'x';
}

/**
 * Drops `name` into group `groupId`: before or after the element `over`
 * (appended when `over` is null or the group has no other element), keeping
 * its width. Dropping before the first cell of a visual row makes the dropped
 * element the one that STARTS that row (`newRow`), taking the flag off the cell
 * that held it, so the row reads as it was drawn; anywhere else it joins a row
 * (`newRow` off). The first element of a group never needs the flag.
 *
 * Returns a new body; the body itself when the drop changes nothing (onto
 * itself, an unknown group).
 */
export function planFormDrop(body, name, { groupId, over = null, side = 'after' }, ctx) {
	const group = groupsOf(body).find(g => g.id === groupId);
	if (!group || over === name) return body;
	const others = entriesOf(group).filter(raw => entryOf(raw).element !== name);
	const overIndex = over ? others.findIndex(raw => entryOf(raw).element === over) : -1;
	const index = overIndex < 0 ? others.length : overIndex + (side === 'before' ? 0 : 1);

	let startsRow = false;
	if (overIndex >= 0 && side === 'before' && index > 0) {
		const cells = groupCells({ elements: others }, ctx);
		const at = cells.findIndex(cell => cell.name === over);
		startsRow = at >= 0 && flowRows(cells).some(row => row[0].index === at);
	}

	let next = moveFormElement(body, name, groupId, index);
	next = setFormElementPlacement(next, name, { newRow: startsRow });
	if (startsRow) next = setFormElementPlacement(next, over, { newRow: false });
	return next;
}

/**
 * One step earlier (-1) or later (+1) in the order, the way a drop would do it
 * (so a key and a drag agree about rows): before the previous element or after
 * the next; at the edge of a group, into the end of the previous group or the
 * start of the next one. Returns the body unchanged when there is nowhere to
 * go.
 */
export function stepFormElement(body, name, direction, ctx) {
	const groups = groupsOf(body);
	const groupIndex = groups.findIndex(group => namesOf(group).includes(name));
	if (groupIndex === -1) return body;
	const names = namesOf(groups[groupIndex]);
	const at = names.indexOf(name);
	const neighbour = names[at + direction];
	if (neighbour) {
		return planFormDrop(body, name, { groupId: groups[groupIndex].id, over: neighbour, side: direction < 0 ? 'before' : 'after' }, ctx);
	}
	const adjacent = groups[groupIndex + direction];
	if (!adjacent) return body;
	const adjacentNames = namesOf(adjacent);
	if (!adjacentNames.length) return moveFormElement(body, name, adjacent.id);
	return direction < 0
		? planFormDrop(body, name, { groupId: adjacent.id, over: adjacentNames[adjacentNames.length - 1], side: 'after' }, ctx)
		: planFormDrop(body, name, { groupId: adjacent.id, over: adjacentNames[0], side: 'before' }, ctx);
}
